/**
 * SalesDashboard.tsx — Sales / Home (redesign slice 3, 2026-09-27): the
 * calm-minimal Minimal board (`docs/design/calm-minimal/Minimal.dc.html`),
 * built to ADOPTION.md §15 and the §22.7 ruling, not to the mockup's numbers.
 *
 * The board, element by element, each on a read path this page already had:
 *   · greeting head — the date line (browser clock), "Good morning" (static,
 *     mockup-verbatim, as before), the session user's first name, and the
 *     INSIGHT sentence: a runtime template over two facts (won this month;
 *     the largest open deals' stage) — homeView.insightSentence;
 *   · four KPI cards, every one a drill (SB-12): Won this month (the current
 *     revenue bucket) with the FACT delta RULED §22.7 — derived from pence,
 *     "September to date", a zero prior ABSENT, nothing that says fixed;
 *     Open pipeline (the fenced Σ, its count and average); Win rate (the
 *     server's closed-cohort trio); Largest open deal (its share, one division
 *     on the GBP leg; the cap sentence at the 200-row ceiling);
 *   · Revenue — the SHARED chart with the boards' empty-month stubs and their
 *     run note, and a legend for the encoding it already had (current month
 *     in --accent, the rest soft);
 *   · Pipeline — the ring (PipelineDonut) over the server's zero-filled spine,
 *     fills bound before zero slices drop; every stage row and the closed-won
 *     row drill as the stage rows did; "Open board ›" goes to the Pipeline;
 *   · Top open deals — share bars (one division) and keyboard-reachable row
 *     drills; "See all N ›" goes to the Pipeline;
 *   · Up next — the earliest PENDING activity with a due date; when none has
 *     one the card says so (Fourth Coffee's live rows carry no due dates);
 *     "See All" keeps the Activities link the feed had;
 *   · Needs attention — a rule over the list: the open value waiting at the
 *     first stage and the companies waiting there;
 *   · the footnote — historyFootnote from the wire's historySince.
 *
 * BLOCKED, and therefore OMITTED (each ruled "omit or render honestly as
 * not-yet", 2026-09-27): "Log a loss" needs a deal picker into the close
 * flow that does not exist — the win-rate note reads "7 won · 0 lost" and
 * stops; "Add to Calendar" has nothing behind it — Calendar stays a
 * coming-soon row in the shell's Tools group, no dead link here; the Needs
 * attention link wants a stage-filtered board that does not exist (slice 4)
 * — the card keeps its two sentences and no link; the Pipeline card's "Open
 * board ›" is the honest route to the unfiltered board.
 *
 * KEPT though the board omits it: "Deals by company" (SB-2 item 5 / SB-13),
 * eight live row drills — where the board omits something live the product
 * wins (ADOPTION.md §12.2), so it rides below the board's rows.
 *
 * The primary action ("+ New Deal") is the shell's slot since slice 2b (4):
 * Dashboard.tsx renders the button in the bar and passes the drawer's open
 * state down; this page keeps the drawer and its refresh.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical design system).
 */
import { useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { DealStageBadge, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchSalesSummary } from '../../api'
import { currencySymbol } from '../../../../lib/money'
import { NewDealDrawer } from './SalesPipeline'
import { topOpenDeals, closedThisMonthPence, dealsByCompany, companyDeals } from './dashboardView'
import { absentMembers, deltaLine, historyFootnote, type DeltaLine, type DeltaTone } from './trendDeltas'
import { ReportsDrillView } from './ReportsDrillView'
import { type ReportsDrill } from './reportsDrill'
import { RevenueByMonthChart } from './RevenueByMonthChart'
import { excludedSuffix } from './fenceCopy'
import { PipelineDonut } from './PipelineDonut'
import {
  dateLine, insightSentence, wonDelta, wonDeltaLine, largestOpenDeal, largestDealNote, sharePct,
  needsAttention, attentionCopy, upNext, dateBox, loggedAgo, initials,
} from './homeView'
import { accent } from '../../../../theme/tokens'

// Takes integer PENCE (the summary/deal money unit since the D9 switch).
// Per-row calls pass the deal's ORIGINAL currency (0111: non-GBP deals
// exist once captured); aggregate figures stay £ — they sum the
// GBP-comparable leg.
export function fmtMoney(pence: number, currency = 'GBP') {
  // SB-10: same swap as fmtCompact — shared symbol rule, spacing and
  // compaction byte-identical.
  const s = currencySymbol(currency)
  const sym = s === currency ? `${s} ` : s
  const n = pence / 100
  if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}k`
  return `${sym}${n.toFixed(0)}`
}

// The k-metric-delta contract: "color = status token" (klaxiora.css). Up and
// down read through the existing trend classes (success / danger tokens),
// flat through the neutral one, and an absent line is quiet ink — the words
// carry the meaning there, not a colour.
const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
  up: 'trend-up', down: 'trend-down', flat: 'trend-neutral', absent: 'text-ink-muted',
}

/**
 * The k-metric widget for the Sales pages (S-Tier slice 3) — the OpsMetric
 * precedent verbatim: it lives WITH the Sales pages and is markup-level reuse
 * of the k-* contract, so DashboardShared's MetricCard and its non-Sales
 * consumers stay untouched. Value with an optional note line and, since 2.1b,
 * the optional `.k-metric-delta` line under the value (§10.C/§11.C reserved
 * the slot): present → "▲ £12k vs 1 Aug"; a card whose metric has no member
 * on the wire, or whose member is absent, passes nothing and renders exactly
 * as before (the page footnote explains an absence). On Sales Home the
 * `.k-home` scope restyles label / delta / note to the boards' sentence-case
 * 13px (klaxiora.css); Reports keeps the mono eyebrow.
 */
export function SalesMetric({ label, value, note, delta, onClick }: {
  label: string; value: string | number; note?: string
  /** 2.1b: the trend line from trendDeltas.deltaLine — null/undefined
   *  renders no line (the wire has no member for this card). */
  delta?: DeltaLine | null
  /** SB-12: a card with a drill behind it renders as a real <button>
   *  (keyboard-reachable). Resting render is class-identical to the div —
   *  .k-metric overrides every button UA style, so the pixel pin holds
   *  (the R7-5 doctrine: affordances are hover/focus-only). */
  onClick?: () => void
}) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="k-metric-label uppercase">{label}</span>
      </div>
      <div className="flex flex-col gap-[7px]">
        <div className="flex items-baseline gap-2">
          <span className="k-metric-value">{value}</span>
        </div>
        {delta && <div className={`k-metric-delta ${DELTA_TONE_CLASS[delta.tone]}`}>{delta.text}</div>}
        {note && <div className="k-metric-note">{note}</div>}
      </div>
    </>
  )
  if (onClick) {
    return (
      <button type="button" onClick={onClick}
        className="k-metric w-full text-left cursor-pointer focus:outline-none">
        {inner}
      </button>
    )
  }
  return <div className="k-metric">{inner}</div>
}

/** The pipeline's first stage — the one "Needs attention" and the insight
 *  sentence reason about (a deal there has not been qualified). The spine's
 *  first open row names it; this is its DealStage label. */
const FIRST_STAGE = 'New Lead'

/** A board card: the boards' 24px surface with a 20px title (`.k-home-card`,
 *  klaxiora.css); the head takes an optional link on the right. */
function HomeCard({ title, sub, link, children, className = '' }: {
  title?: string; sub?: string; link?: { label: string; onClick: () => void }; children: React.ReactNode; className?: string
}) {
  return (
    <section className={`k-card k-home-card ${className}`}>
      {(title || link) && (
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex flex-col gap-[2px] min-w-0">
            {title && <h2 className="k-home-card-title">{title}</h2>}
            {sub && <span className="k-home-card-sub">{sub}</span>}
          </div>
          {link && (
            <button type="button" className="k-home-link" onClick={link.onClick}>{link.label}</button>
          )}
        </div>
      )}
      {children}
    </section>
  )
}

export function SalesDashboard({ onNavigate, onSelectDeal, sessionUser, newDealOpen = false, onNewDealClose, clock }: {
  onNavigate: (page: string) => void
  onSelectDeal?: (id: string) => void
  /** The session user's display name (else email) from the shell's /v1/me
   *  state; null until loaded — the greeting renders nameless. */
  sessionUser?: string | null
  /** Slice 3: the New Deal drawer's open state, held by Dashboard.tsx because
   *  the button lives in the shell's primary-action slot. */
  newDealOpen?: boolean
  onNewDealClose?: () => void
  /** The clock the date line and "Up next" read — Date by default; tests
   *  pass a fixed one (the visual suite pins the browser clock instead). */
  clock?: () => Date
}) {
  // Bumped after a drawer create so useAsync re-runs and the new deal shows.
  const [refreshKey, setRefreshKey] = useState(0)
  // SB-12: in-page drill state (the R7-1..6 shape — no URL router, no
  // cross-page filter navigation). Rendered through the SHARED
  // ReportsDrillView; this page adds no drill machinery of its own.
  const [drill, setDrill] = useState<ReportsDrill | null>(null)
  const { data, loading, error } = useAsync(fetchSalesSummary, [refreshKey])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  if (drill) {
    return <ReportsDrillView drill={drill} onBack={() => setDrill(null)}
      onSelectDeal={onSelectDeal} backLabel="Back to dashboard"
      // SB-13: the company drill filters the SAME list its rollup row was
      // computed from — never a refetch, so drill and number cannot diverge.
      sourceDeals={drill.kind === 'company' ? companyDeals(data!.deals, drill.name) : undefined} />
  }

  const now = (clock ?? (() => new Date()))()
  const { summary, deals, activities } = data!
  const openDeals = topOpenDeals(deals)
  const companyRollup = dealsByCompany(deals)
  // Mockup greets by first name ("Good morning, Dylan"); an email session
  // user greets by the full address rather than a fabricated split.
  const firstName = sessionUser && !sessionUser.includes('@')
    ? sessionUser.trim().split(/\s+/)[0]
    : sessionUser

  const wonPence = closedThisMonthPence(summary.monthlyRevenue)
  const lastBucket = summary.monthlyRevenue.length > 0
    ? summary.monthlyRevenue[summary.monthlyRevenue.length - 1] : null
  const d = summary.deltas
  // RULED 2026-09-26: one footnote for the trend line, the spec's sentence
  // with the ledger's date — never the server's reason sentence (which
  // stays on the wire for support), never an orphan "History since" line.
  const footnote = historyFootnote(summary.historySince, absentMembers(d) > 0)
  // §26 F-1: the fenced counts ride their notes as "(+k excluded)" (F-1d) —
  // nothing at 0. The revenue Σ's own fence (§22.7 (4)) rides the Won card.
  const openExcluded = excludedSuffix(summary.openExcludedNonGbp)
  const wonExcluded = excludedSuffix(summary.excludedNonGbp)
  const openDrill: ReportsDrill = { kind: 'open', openDeals: summary.openDeals, pipelinePence: summary.totalPipeline }
  const won = wonDeltaLine(wonDelta(summary.monthlyRevenue), fmtMoney)
  const largest = largestOpenDeal(deals, summary.totalPipeline)
  const attention = needsAttention(deals, FIRST_STAGE)
  const next = upNext(activities, now.getTime())

  const KPIS: { label: string; value: string; note?: string; drill?: () => void; delta?: DeltaLine | null }[] = [
    // Won this month — the CURRENT bucket of the six-month series, with the
    // §22.7 fact delta beneath it; drills to the month's closed-won rows.
    { label: 'Won this month', value: fmtMoney(wonPence), delta: won, note: wonExcluded || undefined,
      drill: lastBucket ? () => setDrill({ kind: 'month', month: lastBucket.month, wonPence: lastBucket.won }) : undefined },
    // Open pipeline — the fenced Σ; the count and the mean in the note; the
    // 2.1b snapshot delta when the wire has a prior (absent → the footnote).
    { label: 'Open pipeline', value: fmtMoney(summary.totalPipeline),
      note: `${summary.openDeals} ${summary.openDeals === 1 ? 'deal' : 'deals'} · avg ${fmtMoney(summary.avgDealSize)}${openExcluded}`,
      delta: deltaLine(d?.pipelinePence, summary.totalPipeline, fmtMoney), drill: () => setDrill(openDrill) },
    // Win rate — the SERVER closed-cohort rate; absent renders an em dash,
    // never a fake 0%. No "Log a loss": nothing behind it yet (header).
    { label: 'Win rate',
      value: summary.winRateClosedPct != null ? `${Math.round(summary.winRateClosedPct)}%` : '—',
      note: summary.wonCount != null && summary.lostCount != null ? `${summary.wonCount} won · ${summary.lostCount} lost` : undefined,
      drill: summary.wonCount != null && summary.lostCount != null
        ? () => setDrill({ kind: 'closed', wonCount: summary.wonCount!, lostCount: summary.lostCount! }) : undefined },
    // Largest open deal — of the list this page fetched; drills to the deal.
    { label: 'Largest open deal', value: largest ? fmtMoney(largest.deal.amount, largest.deal.currency) : '—',
      note: largest ? largestDealNote(largest) : 'No open deals',
      drill: largest && onSelectDeal ? () => onSelectDeal(largest.deal.id) : undefined },
  ]

  return (
    <div className="k-home page-enter flex flex-col gap-5">
      {/* Greeting head — date line, greeting, the insight sentence. The
          primary action is the shell's slot (Dashboard.tsx). */}
      <div className="flex flex-col gap-[6px] pb-2">
        <span className="text-13 font-semibold text-ink-secondary">{dateLine(now)}</span>
        <h1 className="k-home-title">Good morning{firstName ? `, ${firstName}` : ''}</h1>
        <p className="k-home-insight">{insightSentence({ wonPence, deals, firstStage: FIRST_STAGE }, fmtMoney)}</p>
      </div>

      {/* Four KPI cards — every one drills (SB-12). */}
      <div className="grid grid-cols-4 gap-5">
        {KPIS.map(k => (
          <SalesMetric key={k.label} label={k.label} value={k.value} note={k.note} delta={k.delta} onClick={k.drill} />
        ))}
      </div>

      {/* Row: Revenue | Pipeline ring */}
      <div className="grid grid-cols-[1.6fr_1fr] gap-5">
        <HomeCard title="Revenue" sub="Closed-won · last six months">
          <div className="flex items-center gap-4 text-12 text-ink-secondary" aria-label="Legend">
            <span className="flex items-center gap-[6px]"><span className="w-2 h-2 rounded-full" style={{ background: accent.soft }} aria-hidden="true" />Closed</span>
            <span className="flex items-center gap-[6px]"><span className="w-2 h-2 rounded-full" style={{ background: accent.DEFAULT }} aria-hidden="true" />Month to date</span>
          </div>
          <RevenueByMonthChart data={summary.monthlyRevenue.map(m => ({ month: m.month, wonPence: m.won }))} emptyStubs />
        </HomeCard>
        <HomeCard title="Pipeline" link={{ label: 'Open board ›', onClick: () => onNavigate('sales.pipeline') }}>
          <PipelineDonut funnel={summary.funnel}
            onDrill={r => setDrill({ kind: 'stage', stage: r.stage, label: r.label, count: r.count })} />
        </HomeCard>
      </div>

      {/* Row: Top open deals | Up next + Needs attention */}
      <div className="grid grid-cols-[1.6fr_1fr] gap-5">
        <HomeCard title="Top open deals" link={{ label: `See all ${summary.openDeals} ›`, onClick: () => onNavigate('sales.pipeline') }}>
          {openDeals.length === 0 ? (
            <EmptyState message="No open deals" />
          ) : (
            <div role="table" aria-label="Top open deals" className="flex flex-col">
              <div role="row" className="k-home-deal-grid k-home-deal-head">
                <span role="columnheader">Deal</span><span role="columnheader">Stage</span><span role="columnheader">Share of pipeline</span><span role="columnheader" className="text-right">Value</span>
              </div>
              {openDeals.map(deal => {
                const share = sharePct(deal.basePence, summary.totalPipeline)
                const go = () => onSelectDeal?.(deal.id)
                return (
                  <div key={deal.id} role="row" tabIndex={0} onClick={go}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
                    className="k-home-deal-grid k-home-deal-row cursor-pointer hover:bg-surface-row-hover focus:outline-none">
                    <span role="cell" className="flex items-center gap-3 min-w-0">
                      <span className="k-home-avatar" aria-hidden="true">{initials(deal.company || deal.name)}</span>
                      <span className="flex flex-col min-w-0">
                        <span className="text-14 font-semibold text-ink-primary truncate">{deal.name}</span>
                        <span className="text-12 text-ink-secondary truncate">{deal.company}</span>
                      </span>
                    </span>
                    <span role="cell"><DealStageBadge stage={deal.stage} /></span>
                    <span role="cell" className="flex items-center gap-[10px]">
                      <span className="k-home-share-track flex-1" aria-hidden="true">
                        {share != null && <span className="k-home-share-fill" style={{ width: `${share}%` }} />}
                      </span>
                      <span className="text-13 text-ink-secondary w-9 text-right">{share != null ? `${share}%` : '—'}</span>
                    </span>
                    <span role="cell" className="text-right font-semibold text-ink-primary">{fmtMoney(deal.amount, deal.currency)}</span>
                  </div>
                )
              })}
            </div>
          )}
        </HomeCard>

        <div className="flex flex-col gap-5">
          {/* Up next — the boards' inverted card. The earliest PENDING activity
              with a due date; the honest empty when none has one. "See All"
              keeps the Activities link the feed carried. */}
          <section className="k-card k-home-card k-home-inverted">
            <div className="flex items-center justify-between gap-3">
              <span className="text-13 font-semibold k-home-inverted-muted">Up next</span>
              <button type="button" className="k-card-link cursor-pointer bg-transparent border-0 p-0 uppercase hover:underline k-home-inverted-link"
                onClick={() => onNavigate('sales.activities')}>See All</button>
            </div>
            {next.next ? (
              <div className="flex items-center gap-4">
                <div className="k-home-datebox" aria-hidden="true">
                  <span className="k-home-datebox-mon">{dateBox(next.next.activity.dueAt!).mon}</span>
                  <span className="k-home-datebox-day">{dateBox(next.next.activity.dueAt!).day}</span>
                </div>
                <div className="flex flex-col gap-[2px] min-w-0">
                  <span className="k-home-17 font-semibold truncate">{next.next.activity.subject}</span>
                  <span className="text-13 k-home-inverted-muted">
                    {next.next.activity.createdAt ? loggedAgo(next.next.activity.createdAt, now.getTime()) : 'Pending'}{next.next.overdue ? ' · overdue' : ''}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-[2px]">
                <span className="k-home-17 font-semibold">Nothing scheduled</span>
                <span className="text-13 k-home-inverted-muted">
                  {next.pendingCount === 0
                    ? 'No pending activities.'
                    : `${next.pendingWithoutDue} pending ${next.pendingWithoutDue === 1 ? 'activity has' : 'activities have'} no due date.`}
                </span>
              </div>
            )}
            {/* No "Add to Calendar": nothing is behind it (header). */}
          </section>

          {/* Needs attention — a rule over the list. No link: the stage-
              filtered board it would open does not exist yet (slice 4). */}
          <section className="k-card k-home-card" style={{ padding: '24px 28px' }}>
            <div className="flex items-center gap-2">
              <span className="k-home-attn" aria-hidden="true"><AlertCircle size={14} /></span>
              <span className="text-13 font-semibold text-ink-secondary">Needs attention</span>
            </div>
            {attention ? (
              <>
                <span className="k-home-17 font-semibold text-ink-primary" style={{ letterSpacing: '-0.01em' }}>{attentionCopy(attention, FIRST_STAGE, fmtMoney).headline}</span>
                <span className="text-14 text-ink-secondary" style={{ lineHeight: 1.4 }}>{attentionCopy(attention, FIRST_STAGE, fmtMoney).body}</span>
              </>
            ) : (
              <>
                <span className="k-home-17 font-semibold text-ink-primary" style={{ letterSpacing: '-0.01em' }}>Nothing waiting at {FIRST_STAGE}</span>
                <span className="text-14 text-ink-secondary" style={{ lineHeight: 1.4 }}>Every open deal has been qualified.</span>
              </>
            )}
          </section>
        </div>
      </div>

      {/* Deals by company (SB-2 item 5) — client-side rollup over the page's
          own deals fetch (recon decision: no endpoint at this scale). Kept
          below the board's rows: the board omits it and the product wins
          where the board omits something live (ADOPTION.md §12.2).
          String-grouped: company is free text on the deal, so case/spelling
          variants split rows — the Top-performers v1 limit, worn openly. */}
      <HomeCard title="Deals by company" sub="All deals, grouped by the company on the deal">
        {companyRollup.length === 0 ? (
          <EmptyState message="No deals yet" />
        ) : (
          <table className="closr-table sales-table w-full">
            <thead>
              <tr>
                <th className="text-left">Company</th>
                <th className="text-right">Deals</th>
                <th className="text-right">Won</th>
                <th className="text-right">Open</th>
                <th className="text-right">Open pipeline</th>
                <th className="text-right">Won value</th>
              </tr>
            </thead>
            <tbody>
              {companyRollup.slice(0, 8).map(c => {
                // SB-13: rows drill to the company's deals — the Top-
                // performers tr pattern (a <button> cannot legally wrap a
                // table row); keyboard-reachable, resting classes carry
                // only hover/cursor affordances so the pixel pin holds.
                const go = () => setDrill({
                  kind: 'company', name: c.company, total: c.total, won: c.won,
                  open: c.open, pipelinePence: c.pipelinePence, wonPence: c.wonPence,
                })
                return (
                  <tr key={c.company} tabIndex={0} onClick={go}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
                    className="cursor-pointer hover:bg-surface-row-hover transition-colors focus:outline-none">
                    <td className="font-medium text-ink-primary">{c.company}</td>
                    <td className="text-right text-13 text-ink-body">{c.total}</td>
                    <td className="text-right text-13 text-ink-body">{c.won}</td>
                    <td className="text-right text-13 text-ink-body">{c.open}</td>
                    <td className="text-right font-semibold text-ink-primary">{fmtMoney(c.pipelinePence)}</td>
                    <td className="text-right text-13 text-ink-body">{fmtMoney(c.wonPence)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        {companyRollup.length > 8 && (
          <div className="text-12 text-ink-muted">and {companyRollup.length - 8} more companies</div>
        )}
      </HomeCard>

      {/* The trend footnote (the spec's sentence): the ledger's first day and,
          while any trend is still pending, when trends appear — never hidden
          while the wire carries historySince; absent only on a pre-2.1b
          backend. */}
      {footnote && <p className="text-12 text-ink-muted">{footnote}</p>}

      {newDealOpen && (
        <NewDealDrawer
          initialStage="New Lead"
          onClose={() => onNewDealClose?.()}
          onCreated={() => setRefreshKey(k => k + 1)}
        />
      )}
    </div>
  )
}
